import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle, Video, Globe, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useSettings } from '../context/SettingsContext';
import { submitContact } from '../services/api';
import { useRecaptcha } from '../hooks/useRecaptcha';
import usePageSEO from '../hooks/usePageSEO';

const ICON_MAP = {
  map: MapPin,
  phone: Phone,
  email: Mail,
  clock: Clock,
  whatsapp: MessageCircle,
  video: Video,
  globe: Globe,
  message: MessageSquare,
};

const ICON_COLORS = {
  map: 'text-red-600',
  phone: 'text-blue-600',
  email: 'text-red-600',
  clock: 'text-orange-600',
  whatsapp: 'text-green-600',
  video: 'text-purple-600',
  globe: 'text-indigo-600',
  message: 'text-teal-600',
};

const isSafeAction = (value) => {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  const lowered = trimmed.toLowerCase();
  if (lowered.startsWith('tel:') || lowered.startsWith('mailto:')) return true;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const normalizeCopy = (value, lang) => (
  lang === 'en' ? value.replace(/\bInquires\b/g, 'Inquiries') : value
);

const Contact = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { settings } = useSettings();
  const { execute: executeRecaptcha, enabled: recaptchaEnabled } = useRecaptcha();
  const lang = i18n.language?.substring(0, 2) || 'en';

  usePageSEO({
    title: t('pages.contactTitle'),
    description: t('pages.contactDesc'),
    keywords: 'contact itronix, car electronics bahrain'
  });

  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const recaptchaToken = await executeRecaptcha('contact');
      await submitContact({ ...formData, recaptcha_token: recaptchaToken });
      toast({ title: t('contact.form.successTitle'), description: t('contact.form.successDesc') });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast({ title: "Error", description: "Failed to send message.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getBilingual = (val) => {
    const value = typeof val === 'object' && val !== null ? val[lang] || val.en || '' : val || '';
    return normalizeCopy(value, lang);
  };

  const shopName = lang === 'ar' && settings.site_name_ar ? settings.site_name_ar : settings.site_name || 'Itronix Electronics';
  const shopAddress = settings.address || '';
  const mapQuery = encodeURIComponent(`${shopName}, ${shopAddress}`);
  const mapUrl = `https://maps.google.com/maps?q=${mapQuery}&t=&z=${settings.map_zoom || 15}&ie=UTF8&iwloc=B&output=embed`;
  const mapsLink = `https://www.google.com/maps/search/${mapQuery}`;

  const cards = [...(settings.contact_cards || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="contact-page">
      <div className="bg-gradient-to-br from-red-600 to-red-700 text-white py-16 mb-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('contact.title')}</h1>
          <p className="text-xl text-red-100 mx-auto">{t('contact.description')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {cards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {cards.map((card, idx) => {
              const IconComp = ICON_MAP[card.icon] || MapPin;
              const iconColor = ICON_COLORS[card.icon] || 'text-red-600';
              const title = getBilingual(card.title);
              const content = getBilingual(card.content);
              const subContent = getBilingual(card.sub_content);
              const actionLabel = getBilingual(card.action_label);
              const action = isSafeAction(card.action) ? card.action : '';
              const subAction = isSafeAction(card.sub_action) ? card.sub_action : '';

              // Auto-fill map action if icon is map and no action set
              const resolvedAction = (card.icon === 'map' && !action) ? mapsLink : action;

              return (
                <Card key={idx} className="hover:shadow-lg transition-shadow" data-testid={`contact-card-${idx}`}>
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComp className={`h-7 w-7 ${iconColor}`} />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                    {resolvedAction ? (
                      <a href={resolvedAction} target={resolvedAction.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                        className="text-red-600 hover:underline text-sm block">
                        {content}
                      </a>
                    ) : (
                      <p className="text-gray-600 text-sm">{content}</p>
                    )}
                    {subContent && subAction && (
                      <a href={subAction} className="text-red-600 hover:underline text-sm block mt-1">{subContent}</a>
                    )}
                    {subContent && !subAction && (
                      <p className="text-gray-500 text-sm mt-1">{subContent}</p>
                    )}
                    {resolvedAction && actionLabel && (
                      <a href={resolvedAction} target={resolvedAction.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="mt-3 border-red-600 text-red-600 hover:bg-red-600 hover:text-white">{actionLabel}</Button>
                      </a>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('contact.form.title')}</h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.name')} *</label>
                  <Input name="name" value={formData.name} onChange={handleChange} required data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.email')} *</label>
                  <Input type="email" name="email" value={formData.email} onChange={handleChange} required data-testid="contact-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.phoneLabel')}</label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.subject')} *</label>
                  <Input name="subject" value={formData.subject} onChange={handleChange} required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.form.message')} *</label>
                <Textarea name="message" value={formData.message} onChange={handleChange} required rows={6} />
              </div>
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={isSubmitting} data-testid="contact-submit-btn">
                {isSubmitting ? t('contact.form.sending') : (<><Send className="mr-2 h-5 w-5" />{t('contact.form.sendBtn')}</>)}
              </Button>
            </form>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('contact.mapTitle')}</h2>
            <div className="google-map-embed rounded-lg overflow-hidden shadow-lg h-[500px]">
              <iframe src={mapUrl} width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Location" />
            </div>
            <a href={mapsLink} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center text-red-600 hover:underline text-sm">
              <MapPin className="h-4 w-4 mr-1" />{t('contact.openMaps', 'Open in Google Maps')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
